/*

& PriceBreakdownLines: what a paid action costs, line by line (30 Oct 2026), the same lines the apps show. Used by the
& broadcast preview and the Requirements Feed claim dialog; the figures come from the server's breakdown
& (lib/payments/featurePricing.ts) and are shown as they are, never worked out here.

  base line        "500 agents × ₹2" / "Claim price" / "Ad budget", and its amount
  Platform fee     always shown, ₹0 included
  Pre-sales support  only when chosen
  the offer        its label and what it takes off, when one applies
  GST              its own line when added on top; a caption under the total when the prices already include it
  Total            the list total struck through beside it when an offer applies

*/

import { Typography, useTheme } from "@mui/material";
import { styled } from "@mui/system";
import React from "react";

import { PriceBreakdownPayload, formatBreakdownAmount } from "../../lib/payments/priceBreakdownPayload";

const Row = styled("div")({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "baseline",
	width: "100%",
	gap: "1rem",
});

const Lines = styled("div")({
	display: "flex",
	flexDirection: "column",
	gap: "0.375rem",
	width: "100%",
});

const PriceBreakdownLines = ({
	breakdown,
	baseLabel,
	locale,
	offerColor,
}: {
	breakdown: PriceBreakdownPayload;
	/** What the base amount is ("500 agents × ₹2", "Claim price"). */
	baseLabel: string;
	locale: string | undefined;
	/** The ink for the offer line; the theme's success colour by default. */
	offerColor?: string;
}) => {
	const theme = useTheme();
	const offerInk = offerColor || theme.palette.success.main;
	const lang = {
		platform_fee: locale === "ar-AE" ? "رسوم المنصة" : locale === "kn-IN" ? "ಪ್ಲಾಟ್‌ಫಾರ್ಮ್ ಶುಲ್ಕ" : "Platform fee",
		presales: locale === "ar-AE" ? "دعم ما قبل البيع" : locale === "kn-IN" ? "ಪ್ರೀ-ಸೇಲ್ಸ್ ಬೆಂಬಲ" : "Pre-sales support",
		offer: locale === "ar-AE" ? "عرض" : locale === "kn-IN" ? "ಆಫರ್" : "Offer",
		total: locale === "ar-AE" ? "المجموع" : locale === "kn-IN" ? "ಒಟ್ಟು" : "Total",
		includes: locale === "ar-AE" ? "شامل" : locale === "kn-IN" ? "ಸೇರಿದಂತೆ" : "Includes",
	};
	const money = (amount: number) => formatBreakdownAmount(amount, breakdown.currency);
	const taxLabel = `${breakdown.tax.tax_name} (${breakdown.tax.tax_percentage}%)`;

	return (
		<Lines>
			<Row>
				<Typography variant="body2">{baseLabel}</Typography>
				<Typography variant="body2">{money(breakdown.base_amount)}</Typography>
			</Row>
			<Row>
				<Typography variant="body2">
					{lang.platform_fee + (breakdown.platform_fee.type === "percentage" ? ` (${breakdown.platform_fee.value}%)` : "")}
				</Typography>
				<Typography variant="body2">{money(breakdown.platform_fee.amount)}</Typography>
			</Row>
			{breakdown.presales_support > 0 ? (
				<Row>
					<Typography variant="body2">{lang.presales}</Typography>
					<Typography variant="body2">{money(breakdown.presales_support)}</Typography>
				</Row>
			) : null}
			{breakdown.offer ? (
				<Row>
					<Typography
						variant="body2"
						sx={{ color: offerInk, fontWeight: 600 }}
					>
						{breakdown.offer.label || lang.offer}
					</Typography>
					<Typography
						variant="body2"
						sx={{ color: offerInk, fontWeight: 600 }}
					>
						{"−" + money(breakdown.offer.discount_amount)}
					</Typography>
				</Row>
			) : null}
			{!breakdown.tax_inclusive ? (
				<Row>
					<Typography variant="body2">{taxLabel}</Typography>
					<Typography variant="body2">{money(breakdown.tax.tax_amount)}</Typography>
				</Row>
			) : null}
			<Row>
				<Typography
					variant="body1"
					sx={{ fontWeight: 700 }}
				>
					{lang.total}
				</Typography>
				<Typography
					variant="body1"
					sx={{ fontWeight: 700 }}
				>
					{breakdown.offer ? (
						<span style={{ textDecoration: "line-through", fontWeight: 400, opacity: 0.6, marginRight: "0.5rem" }}>
							{money(breakdown.list_total)}
						</span>
					) : null}
					{money(breakdown.total)}
				</Typography>
			</Row>
			{breakdown.tax_inclusive && breakdown.tax.tax_percentage > 0 ? (
				<Typography
					variant="caption"
					sx={{ opacity: 0.7, alignSelf: "flex-end" }}
				>
					{`${lang.includes} ${taxLabel}: ${money(breakdown.tax.tax_amount)}`}
				</Typography>
			) : null}
		</Lines>
	);
};

export default PriceBreakdownLines;
